// Recomputes size-chip inventory states the instant a colour is tapped.
//
// The theme's variant-picker already round-trips to the server and morphs
// the picker with fresh markup on every option change, and that server
// render is the source of truth — snippets/shabibi-size-options.liquid
// resolves the same five states in Liquid. This exists purely to close the
// latency gap: a size that's sold out in Sage but in stock in Cornflower
// should read correctly on the tap, not one fetch later.
//
// Because both writers derive from the same rules, the morph landing on top
// of this is a no-op rather than a correction.

const STATES = {
  available: 'available',
  low: 'low',
  soldout: 'soldout',
  unavailable: 'unavailable',
};

class ShabibiSizeStates {
  /**
   * @param {HTMLElement} section - The PDP section root.
   */
  constructor(section) {
    this.section = section;
    this.matrix = null;
    this.optionNames = [];

    const raw = section.querySelector('[data-variant-matrix]');
    if (!raw) return;

    try {
      const parsed = JSON.parse(raw.textContent);
      this.matrix = parsed.variants || [];
      this.optionNames = parsed.optionNames || [];
    } catch (error) {
      // A malformed matrix must not take the page down — the server-rendered
      // states are already correct, they just won't update until the fetch
      // resolves.
      console.warn('Shabibi: could not parse variant matrix', error);
      return;
    }

    this.threshold = Number(section.dataset.lowStockThreshold || 3);
    this.display = section.dataset.lowStockDisplay || 'exact';
    // Read once from the rendered markup (sourced from the section's
    // low_stock_label setting) rather than re-read on every change — matches
    // shabibi-cta.js's "cache labels at init" convention.
    this.lowStockLabel = section.querySelector('[data-size-options]')?.dataset.lowStockLabel || 'Low';

    section.addEventListener('change', (event) => {
      if (!(event.target instanceof HTMLInputElement)) return;
      if (event.target.type !== 'radio') return;
      this.apply();
      this.announce(event.target);
    });

    this.apply();
  }

  /** Index of an option in each variant's `options` array, by name. */
  indexOfOption(predicate) {
    return this.optionNames.findIndex((name) => predicate(String(name).toLowerCase()));
  }

  get colorIndex() {
    return this.indexOfOption(
      (n) => n === 'color' || n === 'colour' || n === 'colorway'
    );
  }

  /** The colour currently checked, or null when the product has no colour option. */
  selectedColor() {
    const index = this.colorIndex;
    if (index === -1) return null;

    const name = this.optionNames[index];
    const checked = this.section.querySelector(
      `input[type="radio"][name="${CSS.escape(String(name))}"]:checked`
    );
    return checked ? checked.value : null;
  }

  /**
   * Resolution order, in the order the states have to be tested:
   *   1. no matching variant        -> unavailable
   *   2. inventory not tracked      -> available (untracked is always sellable)
   *   3. policy "continue"          -> available, and never a count (the
   *                                   number is meaningless when overselling
   *                                   is allowed)
   *   4. qty <= 0                   -> soldout
   *   5. qty <= threshold           -> low
   *   6. otherwise                  -> available
   *
   * @returns {{state: string, qty: number}}
   */
  resolve(color, size, sizeIndex) {
    const colorIndex = this.colorIndex;

    const match = this.matrix.find((variant) => {
      const options = variant.options || [];
      if (options[sizeIndex] !== size) return false;
      if (colorIndex === -1 || color === null) return true;
      return options[colorIndex] === color;
    });

    if (!match) return { state: STATES.unavailable, qty: 0 };
    if (!match.tracked) return { state: STATES.available, qty: 0 };
    if (match.policy === 'continue') return { state: STATES.available, qty: 0 };

    const qty = Math.max(0, Number(match.qty) || 0);
    if (qty <= 0) return { state: STATES.soldout, qty: 0 };
    if (qty <= this.threshold) return { state: STATES.low, qty };
    return { state: STATES.available, qty };
  }

  apply() {
    const color = this.selectedColor();

    this.section.querySelectorAll('[data-size-options]').forEach((group) => {
      const fieldset = group.closest('fieldset');
      const legend = fieldset?.querySelector('legend');
      const optionName = legend ? legend.textContent.trim().toLowerCase() : '';
      const sizeIndex = this.indexOfOption((n) => n === optionName);
      if (sizeIndex === -1) return;

      group.querySelectorAll('[data-size-radio]').forEach((radio) => {
        const label = group.querySelector(`label[for="${CSS.escape(radio.id)}"]`);
        if (!label) return;

        const { state, qty } = this.resolve(color, radio.value, sizeIndex);
        this.paint(radio, label, state, qty);
      });
    });
  }

  paint(radio, label, state, qty) {
    label.dataset.state = state;

    // Only `unavailable` is disabled. A sold-out size stays focusable and
    // clickable on purpose: it's a real choice a shopper can express, and
    // it's where a recovery offer would live.
    radio.disabled = state === STATES.unavailable;

    const showCount = state === STATES.low && this.display !== 'off';
    if (showCount) {
      label.dataset.qty = String(qty);
    } else {
      delete label.dataset.qty;
    }

    let caption = label.querySelector('.shabibi-pill-size-stock');
    if (showCount) {
      if (!caption) {
        caption = document.createElement('span');
        caption.className = 'shabibi-pill-size-stock';
        caption.setAttribute('aria-hidden', 'true');
        label.appendChild(caption);
      }
      caption.textContent = this.display === 'exact' ? `${qty} left` : this.lowStockLabel;
    } else if (caption) {
      caption.remove();
    }

    const glyph = label.querySelector('.shabibi-pill-size-glyph');
    const value = glyph ? glyph.textContent.trim() : radio.value;
    radio.setAttribute('aria-label', value + this.stateLabel(state, qty, showCount));
  }

  stateLabel(state, qty, showCount) {
    if (state === STATES.soldout) return ', sold out';
    if (state === STATES.unavailable) return ', unavailable';
    if (showCount) return this.display === 'exact' ? `, ${qty} left` : ', low stock';
    return '';
  }

  /**
   * Announce on change only. Announcing on focus instead would fire on every
   * arrow-key step through the row, which makes the selector unusable with a
   * screen reader.
   */
  announce(changed) {
    const fieldset = changed.closest('fieldset');
    const region = fieldset?.querySelector('[data-size-announcer]');
    if (!region) return;

    const label = fieldset.querySelector(`label[for="${CSS.escape(changed.id)}"]`);
    region.textContent = label
      ? `${label.querySelector('.shabibi-pill-size-glyph')?.textContent.trim() || changed.value} selected${this.stateLabel(
          label.dataset.state,
          Number(label.dataset.qty || 0),
          Boolean(label.dataset.qty)
        )}`
      : `${changed.value} selected`;
  }
}

document.querySelectorAll('[data-shabibi-pdp]').forEach((section) => {
  new ShabibiSizeStates(section);
});
